import { Controller } from '@hotwired/stimulus';

/**
 * JPKI（マイナンバーカード本人確認）ページのコントローラー
 *
 * new（案内ページ）: 開始ボタンで URL発行 API を呼び、スマホはデジタル認証アプリ起動URLへ遷移、
 * PCはスマホ読み取り用のQRコードを表示し、ステータスが変わったら状況確認ページへ遷移する
 * <div data-controller="jpki-page"
 *      data-jpki-page-create-url-value="/api/v1/customer/jpki_verification"
 *      data-jpki-page-status-url-value="/api/v1/customer/jpki_verification"
 *      data-jpki-page-show-url-value="/investor_registration/jpki_identity_verification">
 *   <button data-jpki-page-target="start" data-action="click->jpki-page#start">...</button>
 *   <p data-jpki-page-target="error" hidden></p>
 * </div>
 *
 * show（状況確認ページ）: 状況確認 API を表示直後とその後定期的にポーリングし、ステータスが変わったらリロードする。
 * 反映待ちが長引いた場合は resume ターゲット（手続き再開の導線）を表示する
 * <div data-controller="jpki-page"
 *      data-jpki-page-status-url-value="/api/v1/customer/jpki_verification"
 *      data-jpki-page-initial-status-value="submitted"
 *      data-jpki-page-poll-on-connect-value="true">
 *   <div data-jpki-page-target="resume" hidden>...</div>
 * </div>
 */
export default class extends Controller {
  static targets = ['start', 'error', 'qrcode', 'qrcodeImage', 'sandboxResult', 'resume'];
  static values = {
    createUrl: String,
    showUrl: String,
    statusUrl: String,
    initialStatus: String,
    interval: { type: Number, default: 10000 },
    pollOnConnect: Boolean,
    // Webhook 未着時の結果取得（フォールバック）は本番で1分間隔のため、それを待ってから再開導線を出す
    resumeDelay: { type: Number, default: 60000 }
  };

  connect() {
    // 状況確認ページ（statusUrl あり）ではポーリングを開始する
    this.startPolling();
    // 各ページのヘッダーでも同じコントローラーが動くため、表示直後の取得は状況確認ページに限る
    if (this.timer && this.pollOnConnectValue) this.poll();

    if (this.hasResumeTarget) {
      this.resumeTimer = setTimeout(() => {
        this.resumeTarget.hidden = false;
      }, this.resumeDelayValue);
    }
  }

  disconnect() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.resumeTimer) {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = null;
    }
  }

  // URL発行 → デジタル認証アプリ起動URLへ遷移
  async start() {
    this.hideError();
    this.startTarget.disabled = true;

    try {
      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        body: this.hasSandboxResultTarget
          ? new URLSearchParams({ sandbox_result: this.sandboxResultTarget.value })
          : undefined
      });
      const body = await response.json();

      if (response.ok && body.data && body.data.redirect_url) {
        if (this.hasSandboxResultTarget) {
          this.sandboxResultTarget.value = body.data.sandbox_result;
          this.sandboxResultTarget.disabled = true;
        }
        if (this.isSmartphone()) {
          window.location.href = body.data.redirect_url;
        } else {
          this.showQrcode(body.data.qr_code_data_uri);
          this.initialStatusValue = body.data.status;
          this.startPolling();
        }
      } else {
        this.showError(body.errors ? body.errors.join('\n') : 'URLの発行に失敗しました。再度お試しください。');
        this.startTarget.disabled = false;
      }
    } catch {
      this.showError('通信エラーが発生しました。時間をおいて再度お試しください。');
      this.startTarget.disabled = false;
    }
  }

  startPolling() {
    if (!this.hasStatusUrlValue || this.timer || !this.pollableStatus()) return;

    this.timer = setInterval(() => this.poll(), this.intervalValue);
  }

  showQrcode(dataUri) {
    if (this.hasQrcodeImageTarget) {
      this.qrcodeImageTarget.src = dataUri || '';
    }

    if (this.hasQrcodeTarget) {
      this.qrcodeTarget.hidden = false;
    }
  }

  isSmartphone() {
    return /Android|iPhone/.test(window.navigator.userAgent);
  }

  async poll() {
    // 応答が間隔より遅い場合に取得を重ねない
    if (this.polling) return;
    this.polling = true;

    try {
      const response = await fetch(this.statusUrlValue, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) return;

      const body = await response.json();
      const status = body.data && body.data.status;

      // ステータスが変わったらサーバー側の表示を反映する。
      // 案内ページ（QR表示中）は中断を含む結果を状況確認ページで案内するため、リロードではなく遷移する
      if (status && status !== this.initialStatusValue) {
        clearInterval(this.timer);
        if (this.hasShowUrlValue) {
          window.location.href = this.showUrlValue;
        } else {
          window.location.reload();
        }
      }
    } catch {
      // 通信エラー時は次回のポーリングに任せる
    } finally {
      this.polling = false;
    }
  }

  // 進行中（applying/submitted）のときのみポーリングする
  pollableStatus() {
    return this.initialStatusValue === 'applying' || this.initialStatusValue === 'submitted';
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.hidden = true;
      this.errorTarget.textContent = '';
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.hidden = false;
      this.errorTarget.textContent = message;
    }
  }
}
